import React, { useRef, useState, useEffect, useMemo, KeyboardEvent, ChangeEvent } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { useEFiling } from '../contexts/EFilingContext';

// A generic, self-contained searchable combobox (options in, selection out) —
// modeled on the CompanySelector.tsx pattern (local search + highlightedIndex +
// keyboard nav + click-outside + focus-on-open) but generalized over its
// options and made WAI-ARIA-combobox conformant (role="combobox" +
// aria-activedescendant), which CompanySelector predates. SOR-160 replaces the
// per-card document-type <select> (and the deleted step-level type-ahead) with
// one instance per document card, so each document has exactly one doc-type
// control and assigning a type is a single action (a click / Enter that fires
// onChange immediately).
export interface ComboboxOption {
  id: string | number;
  name: string;
}

interface DocTypeComboboxProps<T extends ComboboxOption> {
  /** The full option list to offer (already form-group/steering filtered by the caller). */
  options: T[];
  /** The currently-assigned option id (String(option.id)); drives aria-selected. */
  value?: string;
  /** Fires immediately on click / Enter — the single writer path (one action). */
  onChange: (option: T) => void;
  brand: BrandConfig;
  /** Stable per-instance suffix for data-testid + DOM id (the doc id). */
  docId: string;
  /** Accessible name for the input; defaults to "Document type". */
  ariaLabel?: string;
  placeholder?: string;
}

function DocTypeCombobox<T extends ComboboxOption>({
  options,
  value,
  onChange,
  brand,
  docId,
  ariaLabel = 'Document type',
  placeholder = 'Select document type…',
}: DocTypeComboboxProps<T>): React.ReactElement {
  // All state is instance-local — two cards never share search / open state, so
  // typing in one combobox can never narrow or alter another (SOR-160 AC#4).
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // R8 (SPEC-SOR-321): while a doc-type-required item names this document's
  // combobox, the input is marked invalid and points at the banner item. The
  // controlId names the WRAPPER id (the jump/scroll target) — the input itself
  // carries no id, so the association rides the wrapper's naming scheme rather
  // than moving the id off the scroll target. Plain destructure, no `?.` guard:
  // a missing context member should fail loudly.
  const { offendingFieldAria } = useEFiling();

  // Same predicate as the deleted step-level filteredDocTypes: case-insensitive
  // substring match on the name, mid-string matches included.
  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.name.toLowerCase().includes(q));
  }, [options, search]);

  // Close (and clear the query) on an outside click — mirrors CompanySelector.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSearch('');
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset the active option whenever the filtered results change.
  /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- reset navigation index on query change */
  useEffect(() => { setHighlightedIndex(-1); }, [search]);
  /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */

  const commitSelection = (option: T) => {
    onChange(option);
    setOpen(false);
    setSearch('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      setSearch('');
      setHighlightedIndex(-1);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlightedIndex(0);
        return;
      }
      setHighlightedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (event.key === 'Enter') {
      if (open && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        event.preventDefault();
        commitSelection(filteredOptions[highlightedIndex]);
      }
    }
  };

  const activeOption =
    open && highlightedIndex >= 0 ? filteredOptions[highlightedIndex] : undefined;

  return (
    <div
      ref={wrapperRef}
      className="relative"
      data-testid={`doc-type-combobox-${docId}`}
      id={`doc-type-combobox-${docId}`}
    >
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        {...offendingFieldAria(`doc-type-combobox-${docId}`)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        aria-controls={`doc-type-listbox-${docId}`}
        aria-activedescendant={
          activeOption ? `doc-type-option-${docId}-${activeOption.id}` : undefined
        }
        value={search}
        placeholder={placeholder}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        className="w-full px-2 py-1.5 border rounded text-sm"
        style={{ borderColor: brand.colors.border, color: brand.colors.text }}
      />

      {/* Browse-all affordance: the full unfiltered list is reachable from
          inside the control while the query is empty (AC#2). Hidden once the
          filer is typing (they are already narrowing) or the list is open. */}
      {!open && search === '' && options.length > 0 && (
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            inputRef.current?.focus();
          }}
          className="text-xs underline mt-1"
          style={{ color: brand.colors.link }}
        >
          Browse all {options.length} document types
        </button>
      )}

      {open && (
        <div
          id={`doc-type-listbox-${docId}`}
          role="listbox"
          aria-label={ariaLabel}
          className="absolute z-50 mt-1 w-full overflow-y-auto rounded-lg border bg-white shadow-lg"
          style={{ borderColor: brand.colors.border, maxHeight: '240px' }}
        >
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm" style={{ color: brand.colors.textLight }}>
              No matching document types
            </div>
          ) : (
            filteredOptions.map((option, idx) => {
              const isHighlighted = idx === highlightedIndex;
              const isSelected = value !== undefined && String(option.id) === value;
              return (
                <button
                  key={option.id}
                  id={`doc-type-option-${docId}-${option.id}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onClick={() => commitSelection(option)}
                  className="w-full px-3 py-1.5 text-left text-sm transition-colors"
                  style={{
                    backgroundColor: isHighlighted
                      ? `${brand.colors.primary}14`
                      : isSelected
                        ? `${brand.colors.primary}0A`
                        : 'transparent',
                    color: brand.colors.text,
                  }}
                >
                  {option.name}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default DocTypeCombobox;
